import {
  Box,
  Typography,
  Stack,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
} from '@mui/material';
import { CalendarMonthRounded, CloseRounded } from '@mui/icons-material';
import { type MonthlyBreakdownRow } from '../../../../hooks/use-reports';

import { fmt } from './shared';

export interface MonthlyBreakdownDialogProps {
  open: boolean;
  onClose: () => void;
  rows: MonthlyBreakdownRow[];
  totalCommitted: number;
  currency: string;
  isDark: boolean;
}

export function MonthlyBreakdownDialog({
  open,
  onClose,
  rows,
  totalCommitted,
  currency,
  isDark,
}: MonthlyBreakdownDialogProps) {
  const maxAmount = Math.max(...rows.map((r) => r.plannedAmount), 1);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: isDark ? 'rgba(15,15,25,0.97)' : 'background.paper',
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Box sx={{ color: 'primary.main', display: 'flex' }}>
            <CalendarMonthRounded />
          </Box>
          <Box>
            <Typography fontWeight={800} variant="h6">
              Compromisos por recaudar
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Desglose mensual de cuotas activas · Total comprometido:{' '}
              <strong>{fmt(totalCommitted, currency)}</strong>
            </Typography>
          </Box>
        </Stack>
        <IconButton size="small" onClick={onClose}>
          <CloseRounded />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {rows.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }}>
            <CalendarMonthRounded sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
            <Typography>No hay cuotas mensuales registradas en planes activos</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: isDark ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.04)',
                    '& th': {
                      fontWeight: 700,
                      fontSize: 12,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    },
                  }}
                >
                  <TableCell sx={{ pl: 3 }}>Mes</TableCell>
                  <TableCell align="center"># Cuotas</TableCell>
                  <TableCell>Distribución</TableCell>
                  <TableCell align="right">Pagado</TableCell>
                  <TableCell align="right" sx={{ pr: 3 }}>
                    Por recaudar
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const pending = Math.max(0, row.plannedAmount - row.paidAmount);
                  const progressPct =
                    row.plannedAmount > 0
                      ? Math.min(100, (row.paidAmount / row.plannedAmount) * 100)
                      : 0;
                  const barWidth = (row.plannedAmount / maxAmount) * 100;

                  return (
                    <TableRow
                      key={row.monthKey}
                      hover
                      sx={{ '&:last-child td': { borderBottom: 0 } }}
                    >
                      <TableCell sx={{ pl: 3, py: 1.5 }}>
                        <Typography fontWeight={600} fontSize={13}>
                          {row.label}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={row.installmentCount}
                          variant="outlined"
                          sx={{ fontWeight: 700, minWidth: 36 }}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 160 }}>
                        <Stack gap={0.5}>
                          {/* Planned total bar */}
                          <Box
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: isDark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.1)',
                              width: `${barWidth}%`,
                              minWidth: 20,
                            }}
                          />
                          {/* Paid progress */}
                          <LinearProgress
                            variant="determinate"
                            value={progressPct}
                            sx={{
                              height: 4,
                              borderRadius: 3,
                              bgcolor: isDark ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.08)',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: 'success.main',
                                borderRadius: 3,
                              },
                              width: `${barWidth}%`,
                              minWidth: 20,
                            }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="success.main" fontWeight={600}>
                          {fmt(row.paidAmount, currency)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ pr: 3 }}>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color={pending > 0 ? 'text.primary' : 'success.main'}
                        >
                          {fmt(pending, currency)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          de {fmt(row.plannedAmount, currency)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
          <Stack direction="row" gap={3}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Total comprometido
              </Typography>
              <Typography fontWeight={800}>{fmt(totalCommitted, currency)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Total pagado (cuotas)
              </Typography>
              <Typography fontWeight={800} color="success.main">
                {fmt(
                  rows.reduce((s, r) => s + r.paidAmount, 0),
                  currency,
                )}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Saldo por recaudar
              </Typography>
              <Typography fontWeight={800} color="primary.main">
                {fmt(
                  rows.reduce((s, r) => s + Math.max(0, r.plannedAmount - r.paidAmount), 0),
                  currency,
                )}
              </Typography>
            </Box>
          </Stack>
          <Button variant="outlined" onClick={onClose} size="small">
            Cerrar
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
